import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Users, Building2, TrendingUp, AlertCircle } from 'lucide-react';
import { API_BASE } from './bigin/constants';
import { BiginDealDialog } from './bigin/BiginDealDialog';
import { BiginCompanyDialog } from './bigin/BiginCompanyDialog';
import { BiginContactDialog } from './bigin/BiginContactDialog';
import { BiginCompaniesTab } from './bigin/BiginCompaniesTab';
import { BiginContactsTab } from './bigin/BiginContactsTab';
import { BiginDealsTab } from './bigin/BiginDealsTab';
import { BiginPipelineChart } from './bigin/BiginPipelineChart';
import { BiginKpiCards } from './bigin/BiginKpiCards';
import { BiginHeader } from './bigin/BiginHeader';
export default function BiginDashboard() {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [deals, setDeals] = useState([]);
  const [lastSync, setLastSync] = useState(null);
  const [isConfigured, setIsConfigured] = useState(true);
  
  // CRUD Dialog states
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [showCompanyDialog, setShowCompanyDialog] = useState(false);
  const [showDealDialog, setShowDealDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form states
  const [contactForm, setContactForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    city: '',
    country: '',
    description: ''
  });
  
  const [companyForm, setCompanyForm] = useState({
    name: '',
    phone: '',
    website: '',
    city: '',
    country: '',
    description: ''
  });
  
  const [dealForm, setDealForm] = useState({
    name: '',
    stage: 'Inquiry',
    pipeline: 'Pre Arrival and Engagement Standard',
    amount: '',
    closing_date: '',
    description: ''
  });
  
  // Available pipelines - derived from synced deals data
  const [availablePipelines, setAvailablePipelines] = useState([
    'Pre Arrival and Engagement Standard',
    'Sales Pipeline Standard'
  ]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const headers = { };

      // Check configuration status
      const statusRes = await fetch(`${API_BASE}/api/bigin/status`, { headers });
      const statusData = await statusRes.json();
      setIsConfigured(statusData.configured);
      setLastSync(statusData.last_sync);

      if (!statusData.configured) {
        setLoading(false);
        return;
      }

      // Fetch dashboard stats
      const dashRes = await fetch(`${API_BASE}/api/bigin/dashboard`, { headers });
      if (dashRes.ok) {
        const dashData = await dashRes.json();
        setDashboardData(dashData);
      }

      // Fetch contacts
      const contactsRes = await fetch(`${API_BASE}/api/bigin/contacts`, { headers });
      if (contactsRes.ok) {
        const contactsData = await contactsRes.json();
        setContacts(contactsData.contacts || []);
      }

      // Fetch companies
      const companiesRes = await fetch(`${API_BASE}/api/bigin/companies`, { headers });
      if (companiesRes.ok) {
        const companiesData = await companiesRes.json();
        setCompanies(companiesData.companies || []);
      }

      // Fetch deals
      const dealsRes = await fetch(`${API_BASE}/api/bigin/deals`, { headers });
      if (dealsRes.ok) {
        const dealsData = await dealsRes.json();
        const fetchedDeals = dealsData.deals || [];
        setDeals(fetchedDeals);
        
        // Extract unique pipelines from the deals
        const pipelines = [...new Set(fetchedDeals.map(d => d.Pipeline).filter(Boolean))];
        if (pipelines.length > 0) {
          setAvailablePipelines(pipelines);
        }
      }

    } catch (error) {
      toast.error('Failed to fetch Bigin data');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await fetch(`${API_BASE}/api/bigin/sync`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Sync completed successfully!', {
          description: `Contacts: ${data.synced?.contacts || 0}, Companies: ${data.synced?.companies || 0}, Deals: ${data.synced?.deals || 0}`
        });
        await fetchDashboardData();
      } else {
        toast.error('Sync failed', { description: data.error || 'Unknown error' });
      }
    } catch (error) {
      toast.error('Sync failed', { description: error.message });
    } finally {
      setSyncing(false);
    }
  };

  // ==================== CRUD FUNCTIONS ====================
  
  const openCreateContact = () => {
    setEditingItem(null);
    setContactForm({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      city: '',
      country: '',
      description: ''
    });
    setShowContactDialog(true);
  };

  const openEditContact = (contact) => {
    setEditingItem(contact);
    setContactForm({
      first_name: contact.First_Name || '',
      last_name: contact.Last_Name || '',
      email: contact.Email || '',
      phone: contact.Phone || '',
      city: contact.Mailing_City || '',
      country: contact.Mailing_Country || '',
      description: contact.Description || ''
    });
    setShowContactDialog(true);
  };

  const saveContact = async () => {
    if (!contactForm.last_name) {
      toast.error('Last name is required');
      return;
    }
    
    setSaving(true);
    try {
      const url = editingItem 
        ? `${API_BASE}/api/bigin/contacts/${editingItem.id || editingItem.bigin_id}`
        : `${API_BASE}/api/bigin/contacts`;
      
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(contactForm)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(editingItem ? 'Contact updated in Zoho Bigin!' : 'Contact created in Zoho Bigin!');
        setShowContactDialog(false);
        fetchDashboardData(); // Refresh data
      } else {
        toast.error(data.detail || 'Failed to save contact');
      }
    } catch (error) {
      toast.error('Failed to save contact');
    } finally {
      setSaving(false);
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/bigin/contacts/${contactId}`, {
        method: 'DELETE',
        headers: { }
      });
      
      if (response.ok) {
        toast.success('Contact deleted');
        fetchDashboardData();
      } else {
        toast.error('Failed to delete contact');
      }
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  const openCreateCompany = () => {
    setEditingItem(null);
    setCompanyForm({
      name: '',
      phone: '',
      website: '',
      city: '',
      country: '',
      description: ''
    });
    setShowCompanyDialog(true);
  };

  const openEditCompany = (company) => {
    setEditingItem(company);
    setCompanyForm({
      name: company.Account_Name || '',
      phone: company.Phone || '',
      website: company.Website || '',
      city: company.Billing_City || '',
      country: company.Billing_Country || '',
      description: company.Description || ''
    });
    setShowCompanyDialog(true);
  };

  const saveCompany = async () => {
    if (!companyForm.name) {
      toast.error('Company name is required');
      return;
    }
    
    setSaving(true);
    try {
      const url = editingItem 
        ? `${API_BASE}/api/bigin/companies/${editingItem.id || editingItem.bigin_id}`
        : `${API_BASE}/api/bigin/companies`;
      
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(companyForm)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(editingItem ? 'Company updated in Zoho Bigin!' : 'Company created in Zoho Bigin!');
        setShowCompanyDialog(false);
        fetchDashboardData();
      } else {
        toast.error(data.detail || 'Failed to save company');
      }
    } catch (error) {
      toast.error('Failed to save company');
    } finally {
      setSaving(false);
    }
  };

  const openCreateDeal = () => {
    setEditingItem(null);
    setDealForm({
      name: '',
      stage: 'Inquiry',
      pipeline: availablePipelines[0] || 'Pre Arrival and Engagement Standard',
      amount: '',
      closing_date: '',
      description: ''
    });
    setShowDealDialog(true);
  };

  const openEditDeal = (deal) => {
    setEditingItem(deal);
    setDealForm({
      name: deal.Deal_Name || '',
      stage: deal.Stage || 'Inquiry',
      pipeline: deal.Pipeline || availablePipelines[0] || 'Pre Arrival and Engagement Standard',
      amount: deal.Amount || '',
      closing_date: deal.Closing_Date || '',
      description: deal.Description || ''
    });
    setShowDealDialog(true);
  };

  const saveDeal = async () => {
    if (!dealForm.name) {
      toast.error('Deal name is required');
      return;
    }
    
    setSaving(true);
    try {
      const url = editingItem 
        ? `${API_BASE}/api/bigin/deals/${editingItem.id || editingItem.bigin_id}`
        : `${API_BASE}/api/bigin/deals`;
      
      const response = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...dealForm,
          amount: dealForm.amount ? parseFloat(dealForm.amount) : 0
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(editingItem ? 'Deal updated in Zoho Bigin!' : 'Deal created in Zoho Bigin!');
        setShowDealDialog(false);
        fetchDashboardData();
      } else {
        toast.error(data.detail || 'Failed to save deal');
      }
    } catch (error) {
      toast.error('Failed to save deal');
    } finally {
      setSaving(false);
    }
  };

  const deleteDeal = async (dealId) => {
    if (!window.confirm('Are you sure you want to delete this deal?')) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/bigin/deals/${dealId}`, {
        method: 'DELETE',
        headers: { }
      });
      
      if (response.ok) {
        toast.success('Deal deleted');
        fetchDashboardData();
      } else {
        toast.error('Failed to delete deal');
      }
    } catch (error) {
      toast.error('Failed to delete deal');
    }
  };

  const getStageColor = (stage) => {
    const stageColors = {
      'Qualification': 'bg-blue-100 text-blue-700 border-blue-200',
      'Needs Analysis': 'bg-purple-100 text-purple-700 border-purple-200',
      'Value Proposition': 'bg-indigo-100 text-indigo-700 border-indigo-200',
      'Proposal': 'bg-amber-100 text-amber-700 border-amber-200',
      'Negotiation': 'bg-orange-100 text-orange-700 border-orange-200',
      'Closed Won': 'bg-emerald-100 text-emerald-700 border-emerald-200',
      'Closed Lost': 'bg-red-100 text-red-700 border-red-200',
    };
    return stageColors[stage] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const formatCurrency = (amount, currency = 'INR') => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Not Configured State
  if (!isConfigured && !loading) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Card className="border-dashed border-2 border-amber-300 bg-amber-50/50">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-amber-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Zoho Bigin Not Configured</h2>
            <p className="text-gray-600 mb-4 max-w-md mx-auto">
              To use the Bigin dashboard, please configure your Zoho Bigin API credentials in the backend environment variables.
            </p>
            <div className="bg-white rounded-lg p-4 text-left max-w-md mx-auto border">
              <p className="text-sm font-medium text-gray-700 mb-2">Required Environment Variables:</p>
              <code className="text-xs text-gray-600 block space-y-1">
                <span className="block">ZOHO_CLIENT_ID=your_client_id</span>
                <span className="block">ZOHO_CLIENT_SECRET=&lt;your_value_here&gt;</span>
                <span className="block">ZOHO_REFRESH_TOKEN=&lt;your_value_here&gt;</span>
                <span className="block">ZOHO_API_DOMAIN=https://www.zohoapis.com</span>
              </code>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="bigin-dashboard">
      {/* Header */}
      <BiginHeader formatDate={formatDate} handleSync={handleSync} lastSync={lastSync} syncing={syncing} />

      {/* KPI Cards */}
      <BiginKpiCards companies={companies} contacts={contacts} dashboardData={dashboardData} deals={deals} formatCurrency={formatCurrency} loading={loading} />

      {/* Pipeline Visualization */}
      <BiginPipelineChart deals={deals} getStageColor={getStageColor} />

      {/* Data Tabs */}
      <Tabs defaultValue="deals" className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="deals" data-testid="tab-deals">
            <TrendingUp className="w-4 h-4 mr-2" /> Deals
          </TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts">
            <Users className="w-4 h-4 mr-2" /> Contacts
          </TabsTrigger>
          <TabsTrigger value="companies" data-testid="tab-companies">
            <Building2 className="w-4 h-4 mr-2" /> Companies
          </TabsTrigger>
        </TabsList>

        {/* Deals Tab */}
        <BiginDealsTab deals={deals} deleteDeal={deleteDeal} formatCurrency={formatCurrency} formatDate={formatDate} getStageColor={getStageColor} loading={loading} openCreateDeal={openCreateDeal} openEditDeal={openEditDeal} />

        {/* Contacts Tab */}
        <BiginContactsTab contacts={contacts} deleteContact={deleteContact} formatDate={formatDate} loading={loading} openCreateContact={openCreateContact} openEditContact={openEditContact} />

        {/* Companies Tab */}
        <BiginCompaniesTab companies={companies} loading={loading} openCreateCompany={openCreateCompany} openEditCompany={openEditCompany} />
      </Tabs>

      {/* Contact Dialog */}
      <BiginContactDialog contactForm={contactForm} editingItem={editingItem} saveContact={saveContact} saving={saving} setContactForm={setContactForm} setShowContactDialog={setShowContactDialog} showContactDialog={showContactDialog} />

      {/* Company Dialog */}
      <BiginCompanyDialog companyForm={companyForm} editingItem={editingItem} saveCompany={saveCompany} saving={saving} setCompanyForm={setCompanyForm} setShowCompanyDialog={setShowCompanyDialog} showCompanyDialog={showCompanyDialog} />

      {/* Deal Dialog */}
      <BiginDealDialog availablePipelines={availablePipelines} dealForm={dealForm} editingItem={editingItem} saveDeal={saveDeal} saving={saving} setDealForm={setDealForm} setShowDealDialog={setShowDealDialog} showDealDialog={showDealDialog} />
    </div>
  );
}
